/*
 * Copyright (c) 2024 SARL SIGMA POLYNESIA
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import SearchInput, {
  SearchInputOption,
  SearchInputProps,
  SearchInputRenderOption
} from '../../../../client/js/components/form/fields/SearchInput'
import { fetchCity, findManyCities } from '../../lib/rest/cityAPIClient'
import { useQuery } from '@tanstack/react-query'
import { getHalEmbedded } from '@jalik/hal'
import { Group, Text } from '@mantine/core'

export function getCityOptionLabel (city: City) {
  return city.name
}

export function CityOption (props: SearchInputRenderOption<City>) {
  const { option } = props
  const { item } = option
  return (
    <Group>
      {item.name}
      {item.linkedTo
        ? <Text size="sm" c="dimmed">{`(${item.linkedTo})`}</Text>
        : null}
    </Group>
  )
}

export type CityInputProps = Omit<SearchInputProps<City>, 'data'> & {
  queryFilter?: Record<string, any>,
}

function CityInput (props: CityInputProps) {
  const intl = useIntl()
  const {
    id,
    label,
    name = 'cityId',
    onChange,
    queryFilter,
    required,
    value,
    ...others
  } = props

  const fieldId = id || `${name}-field`

  const [searchParams, setSearchParams] = useState({ limit: 5, search: null })

  const listQuery = useQuery({
    enabled: true,
    queryKey: ['cities', queryFilter, searchParams],
    queryFn: () => findManyCities({
      ...queryFilter,
      ...searchParams
    }),
    refetchOnWindowFocus: false
  })

  const data = useMemo(() => (
    (listQuery.data != null ? getHalEmbedded(listQuery.data)?.cities : null) || []
  ), [listQuery.data])

  const options: SearchInputOption<City>[] = useMemo(() => (
    data.map((el) => ({
      item: el,
      label: getCityOptionLabel(el),
      value: String(el.id)
    }))
  ), [data])

  const setSearch = useCallback((search: string) => {
    setSearchParams((s) => ({ ...s, search }))
  }, [])

  const cityQuery = useQuery({
    enabled: value != null && value !== '',
    queryKey: ['city', value],
    queryFn: () => fetchCity(parseInt(value, 10)),
    refetchOnWindowFocus: false
  })
  const { data: city } = cityQuery

  useEffect(() => {
    if (city != null && searchParams.search == null) {
      setSearchParams((s) => ({
        ...s, search: getCityOptionLabel(city)
      }))
    }
  }, [city, searchParams.search])

  return (
    <SearchInput
      id={fieldId}
      label={label || intl.formatMessage({
        id: 'city',
        defaultMessage: 'Ville'
      })}
      {...others}
      data={options}
      name={name}
      onChange={onChange}
      onSearchChange={setSearch}
      renderOption={CityOption}
      required={required}
      searchValue={searchParams.search}
      value={value != null ? String(value) : null}
    />
  )
}

export default CityInput
